'use strict';
//***********************************************************************************
//***********************************************************************************
//**** A general handler for object instances
//***********************************************************************************
//***********************************************************************************

import { cn_add, cn_clone, cn_middle, cn_mul, cn_normal, cn_sub, cnx_clone, } from '../utils/cn_utilities';
import { cn_object_instance } from '../model/cn_object_instance';
import { cn_storey } from '../model/cn_storey';
import { cn_svg_map } from './cn_svg_map';
import { cn_facing_trimming, cn_handler_rotation, cn_handler_wall_distance, cn_pastille, cn_roof_slab, cn_type_pastille, cn_view_overlay, } from '..';
import { cn_handler_space_drag2 } from './cn_handler_space_drag2';
import { cn_handler_wall_drag } from './cn_handler_wall_drag';
import { cn_handler_object } from './cn_handler_object';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_edit_box } from './cn_edit_box';
import { cn_number_input, cn_number_list_input } from './cn_inputs';
import { add_params_pastille } from '../utils/cn_object_edit_box_utils';
import { cn_handler_rotate } from './cn_handler_rotate';
import { cn_handler_translate } from './cn_handler_translate';
import { cn_handler_collision } from './cn_handler_collision';
import { cn_handler_resize } from './cn_handler_resize';

export class cn_object_instance_handler extends cn_edition_handler {
    /**
     * Constructor
     * @param {Array<cn_object_instance>} instances
     * @param {cn_svg_map | cn_view_overlay} map
     * @param {boolean} creation
     * @param {cn_storey} storey
     */
    // @ts-ignore
    constructor(instances, map, creation = false, storey = null) {
        // @ts-ignore
        super(instances, map);
        this._edit_box = null;
        this._view_overlay =
            map && map.constructor == cn_view_overlay ? map : null;
        this.instance = instances.length == 1 ? instances[0] : null;
        this.instances = instances;
        this._storey = storey;
        map = map && map.constructor == cn_svg_map ? map : null;
        this._map = map;
        this._controller = map ? map._controller : null;
        this._transaction_manager =
            instances[0].parent.building.transaction_manager;
        this._handler = null;
        this._measure_handler = null;
        this._ghost = null;
        this._grab_roof_instance = false;

        this.handler_object = null;
        this._drag_and_drop_handler = null;
        this._build_handler();

        this.on("resize_label_clicked", () => this._select_instance_dimensions());
    }

    _build_handler() {
        this._handlers = [];
        this._measure_handler = null;
        this._drag_and_drop_handler = null;
        const obj = this;

        //*** Create a handler for that object, depending on the object's type
        this.drawing_storey = this._storey
            ? this._storey
            : this._map
                ? this._map._storey
                : null;
        if (this.instance) {
            if (this.instance.is_roof()) {
                this._handler = new cn_handler_rotation(this.instance.position, this.instance.orientation, 5, this.instance.object.size[0] + this.instance.object.size[1]);
                this._handler.on('change', () => {
                    this._transaction_manager.push_transaction(
                        "Rotation d'ouvrant de toiture",
                        this.instance.ID
                    );
                    this._transaction_manager.push_item_set(
                        obj.instance,
                        'orientation',
                        (item) => {
                            item.update();
                        }
                    );
                    // @ts-ignore
                    this.instance.orientation = this._handler.angle;
                    this._update_measure_handler();
                    if (this._view_overlay) this._view_overlay.refresh_3d();
                });
                this._measure_handler = new cn_handler_wall_distance(
                    this.instance.parent,
                    this._view_overlay || this._map
                );
            } else {
                // @ts-ignore
                this._scene = this.instance.parent;
                let anchor_position = null;
                let anchor_normal = null;
                if (this.instance.object.place_on_wall()) {
                    anchor_position =
                        this.instance.get_anchor_position();
                    anchor_normal = this.instance.object.get_anchor_normal();
                }
                const handler_object = new cn_handler_object(
                    this.instance.size[0],
                    this.instance.size[1],
                    cnx_clone(this.instance.position),
                    (this.instance.orientation * Math.PI) / 180,
                    anchor_position,
                    anchor_normal
                );
                handler_object.element = this.instance;
                this.handler_object = handler_object;

                if (anchor_position) {
                    this._handler = new cn_handler_wall_drag(
                        this.drawing_storey,
                        this.instance._contact_wall,
                        this.instance._contact_wall_side,
                        this.instance._contact_wall_position,
                        this.instance.height
                    );
                    this._drag_and_drop_handler = this._handler;
                    let resize_handler = new cn_handler_resize(handler_object, this);
                    this._handlers.push(resize_handler);
                    resize_handler.set_inactive_handle(this.instance.object.get_inactive_resize_handle());
                    this._handlers.push(this._handler);
                }
                else {
                    this._handlers.push(new cn_handler_collision(handler_object, this._scene, this));
                    this._handlers.push(new cn_handler_rotate(handler_object, this));
                    this._handlers.push(new cn_handler_resize(handler_object, this));
                    this._drag_and_drop_handler = new cn_handler_translate(handler_object, this);
                    this._handlers.push(this._drag_and_drop_handler);
                }
                this._handlers.forEach(h => h.on("change", () => {
                    this._transaction_manager.push_transaction(
                        "Déplacement d'objet",
                        this.instance.ID
                    );
                    this._transaction_manager.push_item_set(this.instance, ['position', 'orientation', 'height', 'size', '_contact_wall', '_contact_wall_side', '_contact_wall_position']);
                    if (h.constructor == cn_handler_wall_drag) {
                        this.instance.place_on_wall(h.wall, h.wall_side, h.wall_position);

                        //*** Special case for 3D */
                        if (this._view_overlay) {
                            this.instance.height = h.wall_height;
                        }
                    }
                    else {
                        this.instance.position = cn_clone(handler_object.center);
                        this.instance.orientation = handler_object.angle * 180 / Math.PI;
                        this.instance.size[0] = handler_object.width;
                        this.instance.size[1] = handler_object.height;
                        this.instance._contact_wall = null;
                    }
                    this.instance.update_deep();
                    handler_object.center = cnx_clone(this.instance.position);
                    handler_object.angle = this.instance.orientation * Math.PI / 180;
                    handler_object.anchor_position = this.instance.get_anchor_position();

                    if (this._drag_and_drop_handler.constructor == cn_handler_wall_drag) {
                        this._drag_and_drop_handler.set_wall(this.instance._contact_wall, this.instance._contact_wall_side, this.instance._contact_wall_position, this.instance.height);
                    }

                    this._update_measure_handler();
                    if (this._view_overlay) this._view_overlay.refresh_3d();
                    else this._map.refresh();
                }))

                this._measure_handler = new cn_handler_wall_distance(
                    this._scene,
                    this._view_overlay || this._map
                );

            }
            if (this._handler) {
                this._handler['instance'] = this.instance;
            }
            this._handlers.push(this._measure_handler);
            this._update_measure_handler();
            this._measure_handler.on('change', (offset) => {
                this._transaction_manager.push_transaction(
                    "Déplacement d'objet",
                    this.instance.ID
                );
                this._transaction_manager.push_item_set(this.instance, [
                    'position',
                ]);
                this.instance.position = cn_add(this.instance.position, offset);
                this.instance.update_deep();
                this._build_handler();
                if (this._view_overlay) this._view_overlay.refresh_3d();
                else this._map.refresh();
            });


        }

        //*** Edit box for mass selection */
        // @ts-ignore
        const edit_box = new cn_edit_box(this, this.instances, this._readOnly);
        this._edit_box = edit_box;
        this._handlers.push(edit_box);

        if (!this._readOnly) {
            //*** Type pastille */
            if (this.instance) {
                const building = this._map
                    ? this._map._building
                    : this._view_overlay._building;
                const type_pastille = new cn_type_pastille(
                    this.instances,
                    'object',
                    () => [...building.get_objects()],
                    "Modèle d'équipement",
                    this._map
                );
                type_pastille.title = "Modifier le modèle d'équipement";
                edit_box.add_pastille(type_pastille);
                type_pastille.on('type_changed', () => this._build_handler());
            }

            if (this.instance) {
                edit_box.add_lock_pastille(this._transaction_manager);
            }

            edit_box.add_duplicator_pastille();
        }

        //*** parameters */
        if (this.instances[0].object.source && this.instances[0].object.source.product_type) {
            //*** We check that all instances have the same source */
            const source = this.instances[0].object.source;
            if (source.product_type != '' && !this.instances.some(i => i.object.source != source)) {
                //*** We create the parameters pastille */
                edit_box.add_pastille(add_params_pastille(this.instances, this._transaction_manager, this, this._readOnly));
            }
        }

        this._height_pastille = null;

        //*** Altitude edition */
        if (this.instance && !this._readOnly) {
            const z0 = this.instance.object.get_default_height();
            let z = z0 + this.instance.height;

            const height_pastille = new cn_pastille(
                [0, 0],
                'h=' + (z * 100).toFixed(0)
            );
            this._height_pastille = height_pastille;
            edit_box.add_pastille(height_pastille);
            height_pastille.svg_class = 'pastille_background white';
            height_pastille.text_class = 'pastille_text black';
            height_pastille.title = 'Modifier la hauteur';
            height_pastille.clicked = () => {
                const input = new cn_number_input(
                    `Hauteur de l'équipement`,
                    z * 100,
                    'cm',
                    0,
                    0
                );
                input.callback = () => {
                    this._transaction_manager.push_transaction(
                        "Hauteur d'équipement"
                    );
                    this._transaction_manager.push_item_set(this.instance, [
                        'height',
                    ]);
                    let v = input.value / 100;
                    this.instance.height = v - z0;
                    z = z0 + this.instance.height;
                    height_pastille.label = 'h=' + (z * 100).toFixed(0);

                    //*** 3D change ? */
                    if (this._view_overlay) {
                        this._view_overlay.refresh_3d();
                    }
                };
                this.call('number_input', input);
            };
        }



        edit_box.add_select_siblings_pastille('object');
    }

    _select_instance_dimensions() {

        const input_list = [];

        const labels = ['Largeur', 'Longueur', 'Hauteur'];
        for (var k = 0; k < 3; k++) {
            const input_width = new cn_number_input(labels[k], this.instance.size[k], 'm', 2, 0.1, 10);
            const default_width = this.instance.size[k] == this.instance.object.size[k];
            input_width.checkbox_label = "Par défaut";
            input_width.checkbox_status = default_width;
            input_list.push(input_width);
        }

        const input = new cn_number_list_input('Dimensions', input_list);
        input.callback = () => {
            this._transaction_manager.push_transaction('Dimensions d\'objet');
            this._transaction_manager.push_item_set(this.instance, 'size');
            for (var k = 0; k < 3; k++) {
                if (input_list[k].checkbox_status)
                    this.instance.size[k] = this.instance.object.size[k];
                else
                    this.instance.size[k] = input_list[k].value;
            }
            this.instance.update_deep();

            if (this._map) this._map.refresh();
            if (this._view_overlay) {
                this._view_overlay.refresh_3d();
            }
            this._build_handler();
        };
        this.call('number_list_input', input);
    }

    //***********************************************************************************
    //**** Refresh
    //***********************************************************************************
    draw(camera) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.draw(camera);

        if (this._height_pastille && this.instance)
            this._height_pastille.label = 'h=' + ((this.instance.object.get_default_height() + this.instance.height) * 100).toFixed(0);
        if (this.handler_object)
            this.handler_object.center[2] = this.instance.get_altitude(this.drawing_storey);

        if (this._handler)
            this._handler.active = this._handler.visible =
                !this.instance.virtual && !this.instance.locked;
        if (camera.is_3d() && this._handler) {
            this._handler.altitude = this.instance.get_altitude(
                this.drawing_storey
            );
            if (this.instance.space) {
                if (this.instance.object.roof_parallel)
                    // @ts-ignore
                    this._handler.plane_normal = this.instance.space.normal;
                // @ts-ignore
                else this._handler.plane_normal = [0, 0, 1];
            }
        }

        if (camera.is_3d() && this._measure_handler)
            this._measure_handler.altitude = this.instance.get_altitude(
                this.drawing_storey
            );

        if (this._handler && this.instance && this.instance.is_roof()) {
            // @ts-ignore
            this._handler._snap_segments = [];
            if (this.instance.space)
                // @ts-ignore
                this._handler._snap_segments =
                    this.instance.space.get_segments();
        }

        if (this.instance)
            this._handlers.forEach(h => h.locked = this.instance.locked);

        return super.draw(camera);
    }

    //***********************************************************************************
    //**** clear move effects
    //***********************************************************************************
    clear_move() {
        this._ghost = null;
        super.clear_move();
    }

    /**
     * Manage a move. To return 'true' if grab is to be managed.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    move(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.move(mouse_event);

        if (super.move(mouse_event)) return true;

        //*** maybe we are grabbing a roof instance */
        if (this._mouse_over_roof_instance(mouse_event)) return true;

        return false;
    }

    /**
     * Manage a grab. To return 'true' if grab is to be managed.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    grab(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.grab(mouse_event);

        this._grab_roof_instance = false;
        this._ghost = null;

        if (super.grab(mouse_event)) {
            if (this._focus_handler == this._drag_and_drop_handler && this.instance &&
                !this.instance.is_roof())
                mouse_event.drag_and_drop_element = this.instance;
            return true;
        }

        mouse_event.drag_and_drop_element = null;

        //*** Special management for roof instances */
        this._grab_roof_instance = this._mouse_over_roof_instance(mouse_event);
        if (this._grab_roof_instance) return true;

        return false;
    }

    _mouse_over_roof_instance(mouse_event) {
        if (!this.instance || !this.instance.is_roof()) return false;

        if (this._view_overlay)
            return (
                mouse_event.impacts.length &&
                mouse_event.impacts[0].storey_element &&
                mouse_event.impacts[0].storey_element.element == this.instance
            );
        return this.instance.contains(mouse_event.mouse_world);
    }

    /**
     * Manage a drag. Only after a grab that returned true. To return 'true' if drag had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    drag(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drag(mouse_event);

        const res = super.drag(mouse_event);

        //*** special case for roof instances */
        if (this._grab_roof_instance) {
            var slab = null;
            var position = [0, 0];
            //*** 3D change ? */
            if (this._view_overlay) {
                if (mouse_event.impacts.length) {
                    for (var i = 0; i < mouse_event.impacts.length; i++) {
                        //*** skip the object itself */
                        if (
                            mouse_event.impacts[i].storey_element &&
                            mouse_event.impacts[i].storey_element.element ==
                            this.instance
                        )
                            continue;

                        //*** skip facings */
                        const element = mouse_event.impacts[i].storey_element
                            ? mouse_event.impacts[i].storey_element.element
                            : null;
                        if (!element) break;
                        if (element.constructor == cn_facing_trimming) continue;

                        //*** Search for roof slab */
                        if (
                            element.constructor == cn_roof_slab &&
                            mouse_event.impacts[i].storey_element.storey.roof ==
                            this.instance.parent
                        ) {
                            slab = element;
                            position = cn_clone(
                                mouse_event.impacts[i].position
                            );
                        }
                        break;
                    }
                }
            } else {
                // @ts-ignore
                slab = this.instance.parent.find_slab(mouse_event.mouse_world);
                position = cn_clone(mouse_event.mouse_world);
            }
            if (slab) {
                this._transaction_manager.push_transaction(
                    "Déplacement d'objet",
                    this.instance.ID
                );
                this._transaction_manager.push_item_set(this.instance, [
                    'position',
                    'space',
                ]);
                this.instance.position = position;
                this.instance.space = slab;
                // @ts-ignore
                this._handler.center = cn_clone(position);
                this._update_measure_handler();
                if (this._view_overlay) this._view_overlay.refresh_3d();
            }
            return true;
        }

        if (false && this._handler && this._handler == this._focus_handler) {
            if (this.instance.is_roof()) return true;

            this._ghost = null;

            if (res) {
                this._transaction_manager.push_transaction(
                    "Modification d'objet",
                    this.instance.ID
                );
                this._transaction_manager.push_item_set(this.instance, [
                    'position',
                    'orientation',
                    '_contact_wall',
                    'space',
                    'size',
                    'height',
                ]);
                this.instance._contact_wall = null;
                // @ts-ignore
                this.instance.position = cn_clone(this._handler.object.center);
                // @ts-ignore
                this.instance.orientation =
                    // @ts-ignore
                    (this._handler.object.angle * 180) / Math.PI;
                this.instance.space = this._handler['space'];
                // @ts-ignore
                this.instance.size[0] = this._handler.object.width;
                // @ts-ignore
                this.instance.size[1] = this._handler.object.height;
                this.instance.update_deep();

                //*** 3D change ? */
                if (this._view_overlay) {
                    //*** maybe update height ? */
                    if (this.instance.object.get_contact() == 'wall') {
                        const dh = this.instance.object.get_default_height();
                        this.instance.height +=
                            mouse_event.impact_behind.position[2] -
                            this.instance.get_altitude(this.drawing_storey);
                        if (dh + this.instance.height < 0.05)
                            this.instance.height = -dh;
                        if (this._height_pastille) {
                            const z = dh + this.instance.height;
                            this._height_pastille.label =
                                'h=' + (z * 100).toFixed(0);
                        }
                    }

                    this._view_overlay.refresh_3d();
                }

                this._update_measure_handler();
                this.call('change');
                this._pending_changes = true;
            } else this._ghost = mouse_event.mouse_world;
        }
        return true;
    }

    /**
     * Manage a drop. Only after a grab that returned true, and at least one drag. To return 'true' if drop had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean}
     */
    // @ts-ignore
    drop(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drop(mouse_event);

        return true;
    }

    /**
     * During a drag and drop, the owner of the drag and drop may change.
     * Return true to accept owning the drag and drop events.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    grab_element(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.grab_element(mouse_event);

        if (this.instance && this._drag_and_drop_handler && this._drag_and_drop_handler.grab_element(mouse_event)) {
            this.instance.virtual = false;
            this._focus_handler = this._drag_and_drop_handler;
            this._dragging_handler = this._drag_and_drop_handler;
        }
        return true;
    }

    /**
     * Manage a drag, during a drag and drop.
     * This may follow:
     * - Either a grab event, where drag and drop has been triggered (by setting drag_and_drop_element on the mouse event)
     * - Either after an accepted grab_element event.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    drag_element(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drag_element(mouse_event);
        return this.drag(mouse_event);
    }

    /**
     * Manage a drop, in a drag and drop event.
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean}
     */
    // @ts-ignore
    drop_element(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drop_element(mouse_event);
        return true;
    }

    _update_measure_handler() {
        if (!this._measure_handler) return;

        const width = this.instance.size[0];
        const height = this.instance.size[1];
        const position = this.instance.position;
        const angle = (this.instance.orientation * Math.PI) / 180;
        const dx = [Math.cos(angle), Math.sin(angle)];
        const dy = cn_normal(dx);

        const vertices = [];
        for (var n = 0; n < 4; n++) {
            var p = cn_clone(position);
            if (n & 1) p = cn_add(p, cn_mul(dx, 0.5 * width));
            else p = cn_sub(p, cn_mul(dx, 0.5 * width));
            if (n & 2) p = cn_add(p, cn_mul(dy, 0.5 * height));
            else p = cn_sub(p, cn_mul(dy, 0.5 * height));
            vertices.push(p);
        }

        this._measure_handler.segments = [];
        if (this.instance.object.place_on_wall()) {
            this._measure_handler.segments.push([vertices[2], vertices[3]]);
            this._measure_handler.segments.push([vertices[1], vertices[0]]);
        } else {
            this._measure_handler.segments.push([
                vertices[0],
                cn_middle(vertices[0], vertices[2]),
            ]);
            this._measure_handler.segments.push([
                vertices[2],
                cn_middle(vertices[2], vertices[3]),
            ]);
            this._measure_handler.segments.push([
                vertices[3],
                cn_middle(vertices[3], vertices[1]),
            ]);
            this._measure_handler.segments.push([
                vertices[1],
                cn_middle(vertices[1], vertices[0]),
            ]);
        }
    }
}
